import { useState, useEffect } from 'react';
import { useNavigation } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Space,
  Descriptions,
  Tag,
  Row,
  Col,
  message,
  Spin,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';

interface BusTrip {
  id: number;
  busCompany: { id: number; name: string };
  departureRegion: { nameUz: string };
  departureCity: { nameUz: string };
  arrivalRegion: { nameUz: string };
  arrivalCity: { nameUz: string };
  departureBusStation: { nameUz: string } | string | null;
  arrivalBusStation: { nameUz: string } | string | null;
  departureTime: string;
  arrivalTime: string;
  pricePerSeat: number;
  totalSeats: number;
  availableSeats: number;
  busType: string;
  busNumber: string | null;
  status: string;
  description: string | null;
  amenities: string[];
  createdAt: string;
}

interface RegionOption {
  id: number;
  nameUz: string;
}

interface CityOption {
  id: number;
  nameUz: string;
  regionId: number;
}

interface StationOption {
  id: number;
  nameUz: string;
  cityId: number;
}

interface StopRow {
  id?: number;
  cityId: number;
  regionId?: number;
  stationId?: number | null;
  scheduledTime: string | null;
}

interface SegmentPriceRow {
  fromStopIndex: number;
  toStopIndex: number;
  price: number;
}

const statusLabels: Record<string, string> = {
  SCHEDULED: 'Rejalashtirilgan',
  DEPARTED: "Yo'lda",
  COMPLETED: 'Yakunlangan',
  CANCELLED: 'Bekor qilingan',
};

const statusColors: Record<string, string> = {
  SCHEDULED: 'blue',
  DEPARTED: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
};

const statusTransitions: Record<string, string[]> = {
  SCHEDULED: ['DEPARTED', 'CANCELLED'],
  DEPARTED: ['COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

const getStationName = (
  station: { nameUz: string } | string | null | undefined,
): string => {
  if (!station) return '-';
  if (typeof station === 'string') return station;
  return station.nameUz || '-';
};

export const BusTripEdit = () => {
  const { id } = useParams();
  const { show, list } = useNavigation();
  const [form] = Form.useForm();
  const [stopsForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingStops, setSavingStops] = useState(false);
  const [trip, setTrip] = useState<BusTrip | null>(null);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [stations, setStations] = useState<StationOption[]>([]);

  useEffect(() => {
    const fetchTrip = async () => {
      setLoading(true);
      try {
        const [tripRes, stopsRes, regionsRes, citiesRes, stationsRes] =
          await Promise.all([
            apiClient.get(`/admin/bus/trips/${id}`),
            apiClient.get(`/admin/bus/trips/${id}/stops`),
            apiClient.get(`/admin/regions`),
            apiClient.get(`/admin/cities`),
            apiClient.get(`/admin/bus/stations`),
          ]);
        const data = tripRes.data.data || tripRes.data;
        setTrip(data);
        form.setFieldsValue({
          departureTime: data.departureTime ? dayjs(data.departureTime) : null,
          arrivalTime: data.arrivalTime ? dayjs(data.arrivalTime) : null,
          pricePerSeat: data.pricePerSeat,
          busNumber: data.busNumber,
          driverName: data.driverName,
          driverPhone: data.driverPhone,
          description: data.description,
          status: data.status,
        });

        const loadedRegions: RegionOption[] = (
          Array.isArray(regionsRes.data) ? regionsRes.data : []
        ).map((r: any) => ({ id: r.id, nameUz: r.nameUz }));
        setRegions(loadedRegions);

        const loadedCities: CityOption[] = (
          Array.isArray(citiesRes.data) ? citiesRes.data : []
        ).map((c: any) => ({
          id: c.id,
          nameUz: c.nameUz,
          regionId: c.regionId,
        }));
        setCities(loadedCities);
        setStations(
          (Array.isArray(stationsRes.data) ? stationsRes.data : []).map(
            (s: any) => ({ id: s.id, nameUz: s.nameUz, cityId: s.cityId }),
          ),
        );

        const stopsData = stopsRes.data ?? { stops: [], segmentPrices: [] };
        const orderedStops: StopRow[] = (stopsData.stops ?? []).map(
          (s: any) => {
            const city = loadedCities.find((c) => c.id === s.cityId);
            return {
              id: s.id,
              cityId: s.cityId,
              regionId: city?.regionId,
              stationId: s.stationId ?? null,
              scheduledTime: s.scheduledTime,
            };
          },
        );

        const stopIdToIndex = new Map<number, number>();
        orderedStops.forEach((s, i) => {
          if (s.id != null) stopIdToIndex.set(s.id, i);
        });

        const segmentPrices: SegmentPriceRow[] = (
          stopsData.segmentPrices ?? []
        )
          .map((sp: any) => {
            const fromIdx = stopIdToIndex.get(sp.fromStopId);
            const toIdx = stopIdToIndex.get(sp.toStopId);
            if (fromIdx == null || toIdx == null) return null;
            return {
              fromStopIndex: fromIdx,
              toStopIndex: toIdx,
              price: Number(sp.price),
            };
          })
          .filter(Boolean) as SegmentPriceRow[];

        stopsForm.setFieldsValue({
          stops: orderedStops.map((s) => ({
            regionId: s.regionId,
            cityId: s.cityId,
            stationId: s.stationId ?? undefined,
            scheduledTime: s.scheduledTime ? dayjs(s.scheduledTime) : null,
          })),
          segmentPrices,
        });
      } catch {
        message.error("Reys ma'lumotlarini yuklashda xatolik");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchTrip();
  }, [id]);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const payload: any = {
        pricePerSeat: values.pricePerSeat,
        busNumber: values.busNumber || null,
        driverName: values.driverName || null,
        driverPhone: values.driverPhone || null,
        description: values.description || null,
      };

      if (values.departureTime) {
        payload.departureTime = values.departureTime.toISOString();
      }
      if (values.arrivalTime) {
        payload.arrivalTime = values.arrivalTime.toISOString();
      }
      if (values.status && values.status !== trip?.status) {
        payload.status = values.status;
      }

      await apiClient.put(`/admin/bus/trips/${id}`, payload);
      message.success('Reys muvaffaqiyatli yangilandi');
      show('admin/bus/trips', Number(id));
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStops = async () => {
    try {
      const values = await stopsForm.validateFields();
      const stops = (values.stops ?? []) as Array<{
        cityId: number;
        stationId?: number;
        scheduledTime: { toISOString: () => string };
      }>;
      if (stops.length < 2) {
        message.error("Kamida 2 ta to'xtash kerak (jo'nash va yetib borish)");
        return;
      }
      const segmentPrices = (values.segmentPrices ?? []) as SegmentPriceRow[];
      for (const sp of segmentPrices) {
        if (sp.fromStopIndex >= sp.toStopIndex) {
          message.error('Segment narxida "qaerdan" indeksi "qaerga" dan kichik bo\'lishi kerak');
          return;
        }
        if (sp.toStopIndex >= stops.length) {
          message.error('Segment narxida "qaerga" indeksi to\'xtashlar soni ichida bo\'lishi kerak');
          return;
        }
      }

      setSavingStops(true);
      await apiClient.put(`/admin/bus/trips/${id}/stops`, {
        stops: stops.map((s) => ({
          cityId: s.cityId,
          stationId: s.stationId ?? undefined,
          scheduledTime: s.scheduledTime.toISOString(),
        })),
        segmentPrices: segmentPrices.map((sp) => ({
          fromStopIndex: sp.fromStopIndex,
          toStopIndex: sp.toStopIndex,
          price: sp.price,
        })),
      });
      message.success("To'xtashlar va segment narxlari yangilandi");
      // Refresh trip head data (departure/arrival mirrors may have changed).
      const tripRes = await apiClient.get(`/admin/bus/trips/${id}`);
      setTrip(tripRes.data.data || tripRes.data);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "To'xtashlarni saqlashda xatolik");
    } finally {
      setSavingStops(false);
    }
  };

  const allowedStatuses = trip?.status
    ? [trip.status, ...(statusTransitions[trip.status] || [])]
    : [];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!trip) {
    return (
      <Card>
        <p>Reys topilmadi</p>
        <Button onClick={() => list('admin/bus/trips')}>Orqaga</Button>
      </Card>
    );
  }

  const bookedSeats = trip.totalSeats - trip.availableSeats;
  const stopsEditable = bookedSeats === 0;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Reysni tahrirlash #{trip.id}</h2>
        <Space>
          <Button onClick={() => show('admin/bus/trips', Number(id))}>
            Bekor qilish
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => form.submit()}
          >
            Saqlash
          </Button>
        </Space>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card title="Reys ma'lumotlari (o'zgartirilmaydi)">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="ID">{trip.id}</Descriptions.Item>
              <Descriptions.Item label="Kompaniya">
                {trip.busCompany?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Yo'nalish">
                {trip.departureCity?.nameUz} → {trip.arrivalCity?.nameUz}
              </Descriptions.Item>
              <Descriptions.Item label="Viloyat">
                {trip.departureRegion?.nameUz} → {trip.arrivalRegion?.nameUz}
              </Descriptions.Item>
              <Descriptions.Item label="Jo'nash bekati">
                {getStationName(trip.departureBusStation)}
              </Descriptions.Item>
              <Descriptions.Item label="Yetib borish bekati">
                {getStationName(trip.arrivalBusStation)}
              </Descriptions.Item>
              <Descriptions.Item label="Avtobus turi">
                <Tag color={trip.busType === 'VIP' ? 'gold' : 'default'}>
                  {trip.busType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Yaratilgan">
                {new Date(trip.createdAt).toLocaleDateString('uz-UZ')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card title="Tahrirlash">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Jo'nash vaqti"
                    name="departureTime"
                    rules={[{ required: true, message: "Jo'nash vaqtini tanlang" }]}
                  >
                    <DatePicker
                      showTime
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD HH:mm"
                      placeholder="Sana va vaqtni tanlang"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Yetib borish vaqti"
                    name="arrivalTime"
                    rules={[{ required: true, message: 'Yetib borish vaqtini tanlang' }]}
                  >
                    <DatePicker
                      showTime
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD HH:mm"
                      placeholder="Sana va vaqtni tanlang"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Narx (so'm)"
                    name="pricePerSeat"
                    rules={[{ required: true, message: 'Narxni kiriting' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                      }
                      parser={(value) => value!.replace(/\s/g, '') as unknown as 0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Haydovchi F.I.Sh." name="driverName">
                    <Input placeholder="Akramov Sardor" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Haydovchi telefoni" name="driverPhone">
                    <Input placeholder="+998901234567" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Avtobus raqami" name="busNumber">
                    <Input placeholder="01A123AA" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Holat" name="status">
                    <Select
                      options={allowedStatuses.map((s) => ({
                        value: s,
                        label: (
                          <Space>
                            <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag>
                            {s === trip.status && <span style={{ color: '#999' }}>(joriy)</span>}
                          </Space>
                        ),
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Tavsif" name="description">
                <Input.TextArea
                  placeholder="Reys haqida qo'shimcha ma'lumot"
                  rows={3}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card
            title="Yo'nalish to'xtashlari va segment narxlari"
            style={{ marginTop: 24 }}
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingStops}
                disabled={!stopsEditable}
                onClick={handleSaveStops}
              >
                To'xtashlarni saqlash
              </Button>
            }
          >
            {!stopsEditable && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Bu reysda ${bookedSeats} ta band qilingan joy bor. Buyurtmalar bo'lgani uchun to'xtashlar va segment narxlarini o'zgartirib bo'lmaydi.`}
                description="Agar yo'nalishni o'zgartirish kerak bo'lsa, avval buyurtmalarni bekor qiling."
              />
            )}
            {stopsEditable && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Birinchi to'xtash — jo'nash, oxirgisi — yetib borish nuqtasi. Ular o'zgarsa, reysning umumiy jo'nash/yetib borish ma'lumotlari ham yangilanadi."
              />
            )}

            <Form form={stopsForm} layout="vertical" disabled={!stopsEditable}>
              <Form.List name="stops">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <EditStopRowFields
                        key={key}
                        name={name}
                        restField={restField}
                        form={stopsForm}
                        regions={regions}
                        allCities={cities}
                        allStations={stations}
                        onRemove={() => remove(name)}
                      />
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginTop: 8 }}
                    >
                      To'xtash qo'shish
                    </Button>
                  </>
                )}
              </Form.List>

              <EditSegmentPricesEditor
                form={stopsForm}
                allCities={cities}
                allStations={stations}
              />
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const buildStopLabel = (
  stop: { cityId?: number; stationId?: number | null } | undefined,
  index: number,
  allCities: { id: number; nameUz: string }[],
  allStations: { id: number; nameUz: string }[],
): string => {
  if (!stop || stop.cityId == null) return `#${index + 1} — (tanlanmagan)`;
  const city = allCities.find((c) => c.id === stop.cityId)?.nameUz ?? '?';
  const station = stop.stationId
    ? allStations.find((s) => s.id === stop.stationId)?.nameUz
    : undefined;
  return station
    ? `#${index + 1}. ${city} — ${station}`
    : `#${index + 1}. ${city}`;
};

const EditStopRowFields = ({
  name,
  restField,
  form,
  regions,
  allCities,
  allStations,
  onRemove,
}: {
  name: number;
  restField: object;
  form: any;
  regions: RegionOption[];
  allCities: CityOption[];
  allStations: StationOption[];
  onRemove: () => void;
}) => {
  const regionId = Form.useWatch(['stops', name, 'regionId'], form) as
    | number
    | undefined;
  const cityId = Form.useWatch(['stops', name, 'cityId'], form) as
    | number
    | undefined;

  const cityOptions = (
    regionId ? allCities.filter((c) => c.regionId === regionId) : allCities
  ).map((c) => ({ value: c.id, label: c.nameUz }));

  const stationOptions = (
    cityId ? allStations.filter((s) => s.cityId === cityId) : []
  ).map((s) => ({ value: s.id, label: s.nameUz }));

  return (
    <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
      <Col span={1} style={{ textAlign: 'center', color: '#888' }}>
        #{name + 1}
      </Col>
      <Col span={4}>
        <Form.Item
          {...restField}
          name={[name, 'regionId']}
          style={{ marginBottom: 0 }}
        >
          <Select
            allowClear
            showSearch
            placeholder="Viloyat"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={() => {
              const stops = form.getFieldValue('stops') ?? [];
              stops[name] = {
                ...(stops[name] ?? {}),
                cityId: undefined,
                stationId: undefined,
              };
              form.setFieldsValue({ stops });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item
          {...restField}
          name={[name, 'cityId']}
          rules={[{ required: true, message: 'Shaharni tanlang' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            showSearch
            placeholder="Shahar"
            options={cityOptions}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(picked) => {
              const stops = form.getFieldValue('stops') ?? [];
              const city = allCities.find((c) => c.id === picked);
              stops[name] = {
                ...(stops[name] ?? {}),
                regionId: city?.regionId ?? stops[name]?.regionId,
                stationId: undefined,
              };
              form.setFieldsValue({ stops });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item
          {...restField}
          name={[name, 'stationId']}
          style={{ marginBottom: 0 }}
        >
          <Select
            allowClear
            showSearch
            placeholder={cityId ? 'Avtovokzal (ixtiyoriy)' : 'Avval shaharni tanlang'}
            disabled={!cityId}
            options={stationOptions}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={cityId ? 'Avtovokzal topilmadi' : null}
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item
          {...restField}
          name={[name, 'scheduledTime']}
          rules={[{ required: true, message: 'Vaqtni tanlang' }]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            format="YYYY-MM-DD HH:mm"
            placeholder="Sana va vaqt"
          />
        </Form.Item>
      </Col>
      <Col span={2} style={{ textAlign: 'right' }}>
        <Button
          danger
          type="text"
          icon={<MinusCircleOutlined />}
          onClick={onRemove}
        />
      </Col>
    </Row>
  );
};

const EditSegmentPricesEditor = ({
  form,
  allCities,
  allStations,
}: {
  form: any;
  allCities: CityOption[];
  allStations: StationOption[];
}) => {
  const stops =
    (Form.useWatch('stops', form) as
      | Array<{ cityId?: number; stationId?: number | null }>
      | undefined) ?? [];

  const stopOptions = stops.map((s, i) => ({
    value: i,
    label: buildStopLabel(s, i, allCities, allStations),
  }));

  return (
    <div style={{ marginTop: 24 }}>
      <h4 style={{ marginBottom: 8 }}>Segment narxlari</h4>
      <Alert
        message="Har bir 'qaerdan → qaerga' uchun yo'lovchi to'laydigan narxni kiriting. Yuqorida kiritilgan to'xtashlar (shahar/avtovokzal) ro'yxati pastdagi tanlovlarda chiqadi."
        type="warning"
        showIcon
        style={{ marginBottom: 8 }}
      />
      <Form.List name="segmentPrices">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Row gutter={8} key={key} align="middle" style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'fromStopIndex']}
                    rules={[{ required: true, message: 'Qaerdan' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      placeholder="Qaerdan"
                      options={stopOptions}
                      filterOption={(input, option) =>
                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      notFoundContent={
                        stopOptions.length === 0
                          ? "Avval yuqorida to'xtashlar qo'shing"
                          : 'Topilmadi'
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'toStopIndex']}
                    rules={[{ required: true, message: 'Qaerga' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      placeholder="Qaerga"
                      options={stopOptions}
                      filterOption={(input, option) =>
                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      notFoundContent={
                        stopOptions.length === 0
                          ? "Avval yuqorida to'xtashlar qo'shing"
                          : 'Topilmadi'
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    {...restField}
                    name={[name, 'price']}
                    rules={[{ required: true, message: 'Narx' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                      parser={(v) => v!.replace(/\s/g, '') as unknown as 0}
                      placeholder="Narx (so'm)"
                    />
                  </Form.Item>
                </Col>
                <Col span={2} style={{ textAlign: 'right' }}>
                  <Button
                    danger
                    type="text"
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(name)}
                  />
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
              disabled={stopOptions.length < 2}
            >
              Segment narxi qo'shish
            </Button>
          </>
        )}
      </Form.List>
    </div>
  );
};
